import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  AuditLogQueryDataIn,
  AuditLogQueryDataOut,
  AuditLogQueryDataOutPaginationDataOut,
  Client,
  DownloadAuditLogDataIn,
  PageInfo,
  PermissionsEnum,
  SortingType,
} from '../../../api/api-reference';
import { AuthService } from '../../../services/auth.service';
import { SearchComponent } from '../../core/components/search/search.component';
import { SortComponent } from '../../core/components/sort/sort.component';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { DateRangeInput } from '../../core/models/date-range-input';
import { FileService } from '../../../services/file.service';
import { ClipboardService } from '../../../services/clipboard.service';
import { processDateRange } from '../../../helper/date-utils';
import { generateFileNameAppendix } from '../../../helper/download-filename';

@Component({
  selector: 'app-audit-logs',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AngularSvgIconModule,
    SearchComponent,
    PaginationComponent,
    SortComponent,
    DatepickerComponent
  ],

  templateUrl: './audit-logs.component.html',
  styleUrl: './audit-logs.component.scss'
})

export class AuditLogsComponent implements OnInit {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfReminders: number = 0;
  public lastPageSize: number = 1;
  selectedRowId: string | null = null;
  activeRow: number | null = null;
  reminderData: AuditLogQueryDataOut[] | undefined = [];
  requestData = new AuditLogQueryDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    sorting: undefined,
    startDate: undefined,
    endDate: undefined
  });

  public PermissionsEnum = PermissionsEnum;
  isDatepickerOpen = false;

  constructor(
    private toastr: ToastrService,
    public router: Router,
    public client: Client,
    public authService: AuthService,
    private fileService: FileService,
    public clipboardService: ClipboardService
  ) { }

  ngOnInit(): void {
    this.reloadTable(1);
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;
    this.client.queryAuditLog(this.requestData).subscribe({
      next: (response) => {
        this.reminderData = response.data;
        this.numberOfReminders = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfReminders / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  download() {
    const fileNameApendix = generateFileNameAppendix();

    const data = new DownloadAuditLogDataIn({
      ...this.requestData,
    });
    this.client.downloadAuditLogFile(data).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `AuditLog${fileNameApendix}`
        );
      },
      error: (err) => {
        this.toastr.error(err);
      }
    })
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  filterDatesInTable() {
    this.client.queryAuditLog(this.requestData).subscribe({
      next: (response) => {
        this.reminderData = response.data;
        this.numberOfReminders = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfReminders / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  setActiveRow(rowIndex: number | null) {
    this.activeRow = this.activeRow === rowIndex ? null : rowIndex;
  }
}
